import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { getLearningProgress, ProgressSnapshot } from '../api/learning';
import { getStudents, StudentListItem } from '../api/students';
import ErrorState from '../../../commodity/components/ui/ErrorState';
import LearningProgressSummary from '../components/LearningProgressSummary';
import { useServerClock } from '../learner/learnerTime';
import {
  ENROLLMENT_STATE_LABEL,
  fmtSessionDate,
  JoinState,
  joinState,
} from '../vocabulary/learningVocabulary';
import type { DisplayTimeZone } from '../api/learner';
import { familyZoneLabel, formatTime } from '../time/zonedTime';
import { useLearnerTime } from '../time/useFamilyTime';

/**
 * The family's progress screen — UC-P09 (PRT-09-FR-04..08), per learner.
 *
 * Everything shown is the server's progress snapshot: the next session
 * (FR-06), the progress line (FR-04/05) and the join window (FR-07/08) are all
 * computed and persisted server-side. The only thing this screen works out
 * for itself is whether the clock is inside a window the server gave it, and
 * it re-checks that every 30 seconds so "Entrar a la clase" appears on time.
 *
 * Join is offered only for an Active enrollment (FR-08), and it hands off to
 * the live-class room; it does not admit anyone itself.
 */

const CLOCK_TICK_MS = 30_000;

const JoinLine: React.FC<{ snapshot: ProgressSnapshot; state: JoinState; tz: DisplayTimeZone }> = ({
  snapshot,
  state,
  tz,
}) => {
  if (state === 'OPEN') {
    const bookingId = snapshot.nextSession?.bookingId;
    return bookingId ? (
      <Link
        to={`/sessions/${encodeURIComponent(bookingId)}`}
        className="btn btn-primary btn-sm"
        style={{ textDecoration: 'none' }}
      >
        Entrar a la clase
      </Link>
    ) : (
      <span style={{ fontSize: 14, color: '#666' }}>
        La clase está por empezar, pero todavía no tiene sala asignada. Vuelve a intentarlo en un
        momento.
      </span>
    );
  }
  if (state === 'NOT_YET' && snapshot.joinWindow) {
    return (
      <span style={{ fontSize: 14, color: '#666' }}>
        Podrás entrar a partir de las {formatTime(snapshot.joinWindow.opensAt, tz)}.
      </span>
    );
  }
  if (state === 'CLOSED') {
    return <span style={{ fontSize: 14, color: '#666' }}>Esta clase ya terminó.</span>;
  }
  if (snapshot.enrollmentState && snapshot.enrollmentState !== 'Active') {
    return (
      <span style={{ fontSize: 14, color: '#666' }}>
        La inscripción está {ENROLLMENT_STATE_LABEL[snapshot.enrollmentState].toLowerCase()}, así
        que no hay clases a las que entrar.
      </span>
    );
  }
  return null;
};

const SnapshotCard: React.FC<{ snapshot: ProgressSnapshot; now: Date; tz: DisplayTimeZone }> = ({
  snapshot,
  now,
  tz,
}) => {
  const state = joinState(snapshot, now);
  return (
    <article
      style={{
        border: '1px solid #d1d5da',
        borderRadius: 8,
        padding: 16,
        background: '#fff',
      }}
    >
      <h2 style={{ fontSize: 17, margin: 0 }}>{snapshot.courseTitle || 'Curso'}</h2>

      <LearningProgressSummary snapshot={snapshot} />

      <div style={{ fontSize: 14, color: '#444', marginBottom: 8 }}>
        Próxima sesión:{' '}
        <strong>
          {snapshot.nextSession
            ? fmtSessionDate(snapshot.nextSession.scheduledStart, tz)
            : 'Sin sesiones próximas'}
        </strong>
      </div>
      <JoinLine snapshot={snapshot} state={state} tz={tz} />

      {snapshot.latestLearnerFeedback && (
        <section style={{ marginTop: 12 }}>
          <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>Comentario del profesor</h3>
          <p style={{ margin: 0, fontSize: 14 }}>{snapshot.latestLearnerFeedback.text}</p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#666' }}>
            {fmtSessionDate(snapshot.latestLearnerFeedback.at, tz)} · también lo ve el alumno
          </p>
        </section>
      )}
      {snapshot.latestParentFeedback && (
        <section style={{ marginTop: 12 }}>
          <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>Solo para ti</h3>
          <p style={{ margin: 0, fontSize: 14 }}>{snapshot.latestParentFeedback.text}</p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#666' }}>
            {fmtSessionDate(snapshot.latestParentFeedback.at, tz)} · el alumno no ve este comentario
          </p>
        </section>
      )}

      <p style={{ margin: '12px 0 0', fontSize: 12, color: '#868e96' }}>
        Actualizado: {fmtSessionDate(snapshot.asOf, tz)}
      </p>
    </article>
  );
};

const ParentProgress: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [students, setStudents] = useState<StudentListItem[] | null>(null);
  const [snapshots, setSnapshots] = useState<ProgressSnapshot[] | null>(null);
  const [serverNow, setServerNow] = useState<string | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  // The server's clock, carried forward: the same clock the server's join
  // check uses, so "Entrar" appears when the door is actually open.
  const now = useServerClock(serverNow, CLOCK_TICK_MS);

  const requested = params.get('studentId');
  const selected =
    students?.find((s) => s.studentId === requested)?.studentId ?? students?.[0]?.studentId;

  // The learner's zone, as the server chose it; the clock is the progress envelope's.
  const { tz } = useLearnerTime(selected);

  const loadStudents = useCallback(async () => {
    setError(null);
    try {
      setStudents((await getStudents()) ?? []);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    void loadStudents();
  }, [loadStudents]);

  /** The learner the newest request is for: a slower reply for another is dropped. */
  const requestedFor = useRef<string | null>(null);

  const loadProgress = useCallback(async () => {
    if (!selected) return;
    requestedFor.current = selected;
    setSnapshots(null);
    setError(null);
    try {
      const result = await getLearningProgress(selected);
      // DEC-S3-P08-01: a late reply for learner A must not render under learner B.
      if (requestedFor.current !== selected) return;
      setSnapshots(result?.snapshots ?? []);
      setServerNow(result?.serverNow);
    } catch (err) {
      if (requestedFor.current === selected) setError(err);
    }
  }, [selected]);

  useEffect(() => {
    void loadProgress();
  }, [loadProgress]);

  if (error) {
    return (
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h1>Progreso</h1>
        <ErrorState
          action="cargar el progreso"
          error={error}
          onRetry={students ? loadProgress : loadStudents}
        />
      </div>
    );
  }
  if (!students) return <p>Cargando el progreso…</p>;

  if (students.length === 0) {
    return (
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h1>Progreso</h1>
        <p style={{ color: '#666' }}>
          Todavía no tienes alumnos registrados.{' '}
          <Link to="/parent/students/new">Agrega a tu primer alumno</Link>.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 4 }}>Progreso</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        Lo que el profesor registró de cada clase, la próxima sesión y cuándo puedes entrar.
      </p>
      <p style={{ color: '#666', marginTop: 0, fontSize: 13 }}>{familyZoneLabel(tz)}</p>

      {students.length > 1 && (
        <div role="group" aria-label="Alumno" style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
          {students.map((s) => (
            <button
              key={s.studentId}
              type="button"
              aria-pressed={s.studentId === selected}
              className={`btn btn-sm ${s.studentId === selected ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setParams({ studentId: s.studentId })}
            >
              {s.firstName}
            </button>
          ))}
        </div>
      )}

      {snapshots === null ? (
        <p>Cargando el progreso…</p>
      ) : snapshots.length === 0 ? (
        <p style={{ color: '#666' }}>
          Este alumno todavía no tiene clases activas.{' '}
          <Link to="/parent/explore">Explora cursos</Link>.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {snapshots.map((s) => (
            <SnapshotCard key={s.enrollmentId} snapshot={s} now={now} tz={tz} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ParentProgress;
